import { get } from './https';

export type DashboardPeriod = 'weekly' | 'monthly';
export type DashboardTaskMetric = 'bookings' | 'enquiries';

export type DashboardSummary = {
  totalUsers: number;
  totalTechnicians: number;
  totalBookings: number;
  totalEnquiries: number;
  totalRevenue: number;
  pendingBookings?: number;
  lastWeek: {
    users: number;
    technicians: number;
    bookings: number;
    enquiries: number;
    revenue: number;
  };
};

export type DashboardEnquiryStat = {
  count: number;
  percent: number;
};

export type DashboardWeeklyDay = {
  day: string;
  value: number;
  rawValue: number;
  active: boolean;
};

export type DashboardRevenueDay = {
  day: string;
  dateKey?: string;
  label?: string;
  income: number;
  expenses: number;
};

export type DashboardSegment = {
  label: string;
  key: string;
  percent: number;
  value: string;
  color: string;
  count: number;
};

export type DashboardBookingRow = {
  id: string;
  user: string;
  tech: string;
  contact: string;
  slot: string;
  payment: 'Paid' | 'Pending' | 'Unpaid';
  status?: string;
};

export type DashboardWeeklyTasks = {
  period: DashboardPeriod;
  metric: DashboardTaskMetric;
  chartMax: number;
  series: DashboardWeeklyDay[];
  bookingsSeries?: DashboardWeeklyDay[];
  enquiriesSeries?: DashboardWeeklyDay[];
};

export type DashboardData = {
  filters?: {
    period: DashboardPeriod;
    taskMetric: DashboardTaskMetric;
  };
  summary: DashboardSummary;
  enquiries: {
    total: number;
    new: DashboardEnquiryStat;
    inProgress: DashboardEnquiryStat;
    completed: DashboardEnquiryStat;
    cancelled?: DashboardEnquiryStat;
  };
  weeklyTasks: DashboardWeeklyTasks;
  revenue: {
    period?: DashboardPeriod;
    total: number;
    allTimeTotal: number;
    weekly: DashboardRevenueDay[];
    maxValue: number;
  };
  userPerformance: {
    score: number;
    min: number;
    max: number;
    newUsers: number;
    returningUsers: number;
    totalUsers?: number;
  };
  overallPerformance: {
    totalCount: number;
    segments: DashboardSegment[];
  };
  propertyTypes?: { type: string; count: number; percent: number }[];
  recentBookings: DashboardBookingRow[];
};

type DashboardResponse = {
  status: boolean;
  message: string;
  data: DashboardData;
};

export type DashboardQuery = {
  period?: DashboardPeriod;
  taskMetric?: DashboardTaskMetric;
};

const toSafeNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const normalizePercent = (value: unknown): number =>
  Math.max(0, Math.min(100, toSafeNumber(value)));

const emptyEnquiryStat = (): DashboardEnquiryStat => ({
  count: 0,
  percent: 0,
});

const normalizeEnquiryStat = (value: any): DashboardEnquiryStat => ({
  count: toSafeNumber(value?.count),
  percent: normalizePercent(value?.percent),
});

const defaultWeeklySeries = (): DashboardWeeklyDay[] =>
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => ({
    day,
    value: 0,
    rawValue: 0,
    active: false,
  }));

const normalizeWeeklySeries = (series: any): DashboardWeeklyDay[] => {
  if (!Array.isArray(series)) return defaultWeeklySeries();

  return series.map((item, index) => ({
    day: String(item?.day || item?.label || `D${index + 1}`),
    value: toSafeNumber(item?.value),
    rawValue: toSafeNumber(item?.rawValue ?? item?.value ?? item?.count),
    active: Boolean(item?.active),
  }));
};

const normalizeRevenueSeries = (weekly: any): DashboardRevenueDay[] => {
  if (!Array.isArray(weekly)) {
    return defaultWeeklySeries().map((item) => ({
      day: item.day,
      income: 0,
      expenses: 0,
    }));
  }

  return weekly.map((item, index) => ({
    day: String(item?.day || item?.label || `D${index + 1}`),
    dateKey: item?.dateKey,
    label: item?.label,
    income: toSafeNumber(item?.income),
    expenses: toSafeNumber(item?.expenses),
  }));
};

const normalizeSegments = (segments: any): DashboardSegment[] => {
  if (!Array.isArray(segments)) return [];

  return segments.map((item, index) => {
    const percent = normalizePercent(item?.percent);
    return {
      label: String(item?.label || item?.key || `Segment ${index + 1}`),
      key: String(item?.key || item?.label || `segment_${index + 1}`),
      percent,
      value: String(item?.value || `${percent}%`),
      color: String(item?.color || '#888888'),
      count: toSafeNumber(item?.count),
    };
  });
};

const normalizeRecentBookings = (bookings: any): DashboardBookingRow[] => {
  if (!Array.isArray(bookings)) return [];

  return bookings.map((item) => {
    const payment = ['Paid', 'Pending', 'Unpaid'].includes(item?.payment)
      ? item.payment
      : 'Unpaid';

    return {
      id: String(item?.id || item?.bookingId || '—'),
      user: String(item?.user || item?.userName || '—'),
      tech: String(item?.tech || item?.technicianName || '—'),
      contact: String(item?.contact || item?.phoneNumber || '—'),
      slot: String(item?.slot || '—'),
      payment,
      status: item?.status ? String(item.status) : undefined,
    };
  });
};

const normalizeDashboardData = (
  rawData: Partial<DashboardData> | any,
  query: DashboardQuery,
): DashboardData => {
  const data = rawData || {};
  const period = data.filters?.period || query.period || 'weekly';
  const taskMetric =
    data.filters?.taskMetric || query.taskMetric || 'bookings';

  const summary = data.summary || {};
  const lastWeek = summary.lastWeek || {};

  const weeklyTasksSource = Array.isArray(data.weeklyTasks)
    ? {
        period,
        metric: taskMetric,
        chartMax: 80,
        series: data.weeklyTasks,
      }
    : data.weeklyTasks || {};

  const weeklySeries = normalizeWeeklySeries(weeklyTasksSource.series);
  const revenueWeekly = normalizeRevenueSeries(data.revenue?.weekly);
  const totalRevenue = toSafeNumber(summary.totalRevenue);

  return {
    filters: { period, taskMetric },
    summary: {
      totalUsers: toSafeNumber(summary.totalUsers),
      totalTechnicians: toSafeNumber(summary.totalTechnicians),
      totalBookings: toSafeNumber(summary.totalBookings),
      totalEnquiries: toSafeNumber(summary.totalEnquiries),
      totalRevenue,
      pendingBookings: toSafeNumber(summary.pendingBookings),
      lastWeek: {
        users: toSafeNumber(lastWeek.users),
        technicians: toSafeNumber(lastWeek.technicians),
        bookings: toSafeNumber(lastWeek.bookings),
        enquiries: toSafeNumber(lastWeek.enquiries),
        revenue: toSafeNumber(lastWeek.revenue),
      },
    },
    enquiries: {
      total: toSafeNumber(data.enquiries?.total),
      new: data.enquiries?.new
        ? normalizeEnquiryStat(data.enquiries.new)
        : emptyEnquiryStat(),
      inProgress: data.enquiries?.inProgress
        ? normalizeEnquiryStat(data.enquiries.inProgress)
        : emptyEnquiryStat(),
      completed: data.enquiries?.completed
        ? normalizeEnquiryStat(data.enquiries.completed)
        : emptyEnquiryStat(),
      cancelled: data.enquiries?.cancelled
        ? normalizeEnquiryStat(data.enquiries.cancelled)
        : emptyEnquiryStat(),
    },
    weeklyTasks: {
      period: weeklyTasksSource.period || period,
      metric: weeklyTasksSource.metric || taskMetric,
      chartMax: toSafeNumber(weeklyTasksSource.chartMax) || 80,
      series: weeklySeries,
      bookingsSeries: normalizeWeeklySeries(weeklyTasksSource.bookingsSeries),
      enquiriesSeries: normalizeWeeklySeries(weeklyTasksSource.enquiriesSeries),
    },
    revenue: {
      period: data.revenue?.period || period,
      total: toSafeNumber(data.revenue?.total),
      allTimeTotal: toSafeNumber(data.revenue?.allTimeTotal ?? totalRevenue),
      weekly: revenueWeekly,
      maxValue: toSafeNumber(data.revenue?.maxValue),
    },
    userPerformance: {
      score: toSafeNumber(data.userPerformance?.score),
      min: toSafeNumber(data.userPerformance?.min),
      max: toSafeNumber(data.userPerformance?.max) || 500,
      newUsers: toSafeNumber(data.userPerformance?.newUsers),
      returningUsers: toSafeNumber(data.userPerformance?.returningUsers),
      totalUsers: toSafeNumber(data.userPerformance?.totalUsers),
    },
    overallPerformance: {
      totalCount: toSafeNumber(data.overallPerformance?.totalCount),
      segments: normalizeSegments(data.overallPerformance?.segments),
    },
    propertyTypes: Array.isArray(data.propertyTypes)
      ? data.propertyTypes.map((item: any) => ({
          type: String(item?.type || 'UNKNOWN'),
          count: toSafeNumber(item?.count),
          percent: normalizePercent(item?.percent),
        }))
      : [],
    recentBookings: normalizeRecentBookings(data.recentBookings),
  };
};

export const fetchDashboardStats = async (
  query: DashboardQuery = {},
): Promise<DashboardData> => {
  const params = new URLSearchParams();
  if (query.period) params.set('period', query.period);
  if (query.taskMetric) params.set('taskMetric', query.taskMetric);
  const qs = params.toString();
  const url = `/api/v1/admin/dashboard/stats${qs ? `?${qs}` : ''}`;

  const response = await get(url);
  if (response.status !== 200) {
    throw new Error(
      response.data?.message || 'Failed to load dashboard statistics',
    );
  }

  const rawData = (response.data as DashboardResponse).data || response.data;
  return normalizeDashboardData(rawData, query);
};

export const formatCompactCount = (value: number | null | undefined): string => {
  const safeValue = toSafeNumber(value);
  if (safeValue >= 1_000_000) {
    return `${(safeValue / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (safeValue >= 1000) {
    return `${(safeValue / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return safeValue.toLocaleString('en-IN');
};

export const formatCurrency = (value: number | null | undefined): string =>
  `£${toSafeNumber(value).toLocaleString('en-IN')}`;

export const computeChartMax = (peak: number): number => {
  if (peak <= 0) return 1000;
  if (peak <= 1000) return Math.max(Math.ceil(peak / 250) * 250, 500);
  if (peak <= 5000) return Math.ceil(peak / 500) * 500;
  if (peak <= 10000) return Math.ceil(peak / 1000) * 1000;
  if (peak <= 25000) return Math.ceil(peak / 2500) * 2500;
  return Math.ceil(peak / 5000) * 5000;
};

export const buildRevenueYTicks = (max: number): number[] => {
  const chartMax = Math.max(max, 500);
  const step = chartMax / 4;
  return [1, 2, 3, 4].map((i) => Math.round(step * i));
};

export const buildYTicks = (max: number): number[] => {
  const chartMax = Math.max(max, 10);
  return [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(chartMax * f));
};
